import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { PermissionService } from "../services/permission.service";
import { CurrentUserService } from "../auth/current-user.service";
import { UnauthorizedRedirectService } from "../services/unauthorized-redirect.service";

/**
 * Maps the top URL path segment (entity) to the backend Entity name used in
 * spatie ability strings (e.g. "read_all.Project"). Any segment NOT in this map
 * is treated as UNGUARDED (allowed) — so utility routes like dashboard,
 * calculator, chats, notifications and ask-apro are never blocked.
 */
const ENTITY_MAP: Readonly<Record<string, string>> = {
  projects: "Project",
  properties: "Property",
  contacts: "Contact",
  owners: "Owner",
  buyers: "Buyer",
  tenants: "Tenant",
  users: "User",
  agents: "Agent",
  "agent-groups": "AgentGroup",
  organizations: "Organization",
  roles: "Role",
  permissions: "Permission",
  appointments: "Appointment",
  documents: "Document",
  // NOT mapped (intentionally UNGUARDED by this client guard):
  //  - leads, timeline (and its nested steps), transactions.
  // The backend routes for leads / timelines / timeline-steps are gated by
  // `auth:sanctum` ONLY — there is NO `permission:` middleware and NO
  // Lead / Timeline module in the spatie permission set, so NO role (not even
  // Admin/Director) holds read_*.Lead / read_*.Timeline. Mapping them here made
  // the guard require a permission that does not exist and blocked EVERY role
  // except Superadmin (Admin/Director included) from all leads/timeline/steps
  // pages. The sidebar already shows these to the right roles; access is
  // governed by auth + server-side scoping, not by a page permission.
  // `transactions` is a dead/commented-out route+menu with no Transaction
  // permission held by anyone — mapping it would over-block the same way if the
  // page were ever revived. Leave all three OUT of the map so they are treated
  // as unguarded utility routes.
};

/**
 * Explicit ability overrides for routes whose backend permission does NOT
 * follow the read_all/read_one.<Entity> convention. The value is the FULL
 * ability string required to access any page under that top path segment.
 *
 * Project Submissions are gated by `manage.ProjectSubmission` (list / show /
 * create / update / destroy) and approvals by `approve.ProjectSubmission`
 * (Superadmin-only) — see routes/api.php. Using the CRUD convention here would
 * demand a non-existent `read_all.ProjectSubmission` and block EVERY role
 * (including Admin/Director), which is the 2026-10-17 regression this fixes.
 * A segment present here takes precedence over ENTITY_MAP.
 */
const ABILITY_OVERRIDE: Readonly<Record<string, string>> = {
  "project-submissions": "manage.ProjectSubmission",
  "project-submission-approvals": "approve.ProjectSubmission",
};

/** Segments that indicate a single-record (read_one) route. */
const READ_ONE_SEGMENTS: ReadonlyArray<string> = ["detail", "view", "edit"];

const UNAUTHORIZED_URL = "/pages/unauthorized";

/**
 * Client-side route-permission guard (item 1).
 *
 * Gates feature routes by the logged-in user's stored abilities, mirroring the
 * backend authorization for UX purposes only — the server remains the security
 * boundary (the 403 interceptor still redirects API-denied responses here).
 *
 * Superadmin always bypasses. For a mapped entity segment the guard resolves a
 * required ability (read_one for a detail/view/edit or :id route, otherwise
 * read_all) and denies by redirecting to the unauthorized page when the user
 * lacks it. Unmapped/utility routes are allowed. It never logs the user out.
 */
@Injectable({ providedIn: "root" })
export class PermissionRouteGuard implements CanActivateChild {
  constructor(
    private router: Router,
    private permissionService: PermissionService,
    private currentUser: CurrentUserService,
    private unauthorizedRedirect: UnauthorizedRedirectService,
  ) {}

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree {
    // Superadmin bypass.
    if (this.permissionService.isSuperadmin()) {
      return true;
    }

    const requiredAbility = this.resolveRequiredAbility(state.url);

    // Unmapped / utility route → unguarded.
    if (!requiredAbility) {
      return true;
    }

    const { ability, entity, isReadOne, allowReadAllFallback } =
      requiredAbility;

    // Allow when the user holds the exact ability, or (for a read_one route
    // that permits it) when they hold read_all for the same entity (read_all
    // implies read_one). The read_all fallback is intentionally suppressed for
    // Project detail (see resolveRequiredAbility): a Project detail page is
    // gated on the dedicated page-access permission detail.Project, NOT on the
    // read_one.Project API read that non-superadmins still hold, and holding
    // read_all.Project (for the project LIST) must not leak detail-page access.
    const allowed =
      this.permissionService.hasPermission(ability) ||
      (isReadOne &&
        allowReadAllFallback &&
        this.permissionService.hasPermission("read_all." + entity));

    if (allowed) {
      return true;
    }

    // Deny: record the block (loop detection) and redirect to the unauthorized
    // page. Do NOT log out.
    this.unauthorizedRedirect.markBlocked();
    return this.router.parseUrl(UNAUTHORIZED_URL);
  }

  /**
   * Resolve the required ability from an attempted URL. Returns null when the
   * route's entity segment is not in the map (unguarded).
   */
  private resolveRequiredAbility(url: string): {
    ability: string;
    entity: string;
    isReadOne: boolean;
    allowReadAllFallback: boolean;
  } | null {
    // Strip query/fragment and leading slash, then split into segments.
    const path = url.split("?")[0].split("#")[0];
    const segments = path.split("/").filter((s) => s.length > 0);

    if (segments.length === 0) {
      return null;
    }

    // Explicit override for non-CRUD-named routes (e.g. Project Submissions use
    // manage.ProjectSubmission, not read_all/read_one). isReadOne=false so the
    // read_all fallback in canActivateChild never applies to these.
    const override = ABILITY_OVERRIDE[segments[0]];
    if (override) {
      return {
        ability: override,
        entity: "",
        isReadOne: false,
        allowReadAllFallback: false,
      };
    }

    const entity = ENTITY_MAP[segments[0]];
    if (!entity) {
      return null;
    }

    const rest = segments.slice(1);
    const isReadOne = rest.some(
      (seg) => READ_ONE_SEGMENTS.includes(seg) || /^\d+$/.test(seg),
    );
    const action = isReadOne ? "read_one" : "read_all";

    // Project detail page-access gate.
    // A Project detail/edit route (e.g. /projects/detail/:id) is gated on the
    // dedicated page-access permission `detail.Project` (Superadmin-only),
    // which is DISTINCT from the `read_one.Project` API-read permission that
    // non-superadmin roles still hold for other single-project flows. The
    // read_all fallback is deliberately NOT applied here, so holding
    // `read_all.Project` (needed for the project LIST) does not leak
    // detail-page access. The project LIST (read_all.Project) is unchanged, and
    // the create route /projects/detail (no id) stays Superadmin-only via
    // ProjectCreateGuard (Superadmin also holds detail.Project, so requiring it
    // here is harmless for create). Superadmin still bypasses this guard
    // entirely via the isSuperadmin short-circuit in canActivateChild.
    if (entity === "Project" && isReadOne) {
      return {
        ability: "detail.Project",
        entity,
        isReadOne: true,
        allowReadAllFallback: false,
      };
    }

    return {
      ability: `${action}.${entity}`,
      entity,
      isReadOne,
      allowReadAllFallback: true,
    };
  }
}
